import { type GeoPosition, getPositionPromise } from '../hooks/utils/geolocation';

export interface CachedCoords {
	latitude: number | null;
	longitude: number | null;
	accuracy: number | null;
}

/**
 * Resolves the coordinates to geo-stamp an upload with.
 * Tries a fresh high-accuracy fix first; if that fails (denied/timeout/unavailable)
 * it falls back to the most recent cached tracking coordinates. Returns `null` when
 * neither is available, so callers can block the upload and prompt for GPS.
 */
export const resolveUploadCoords = async (cached?: CachedCoords | null): Promise<GeoPosition | null> => {
	try {
		return await getPositionPromise();
	} catch {
		if (cached?.latitude != null && cached?.longitude != null) {
			return {
				latitude: cached.latitude,
				longitude: cached.longitude,
				accuracy: cached.accuracy ?? 0,
			};
		}
		return null;
	}
};

/**
 * Appends the geo-stamp to a multipart upload using ASP.NET nested-model-binding
 * field names (`Location.Latitude`, `Location.Longitude`). Accuracy is sent as an
 * extra audit signal and is harmlessly ignored if the backend model has no such field.
 */
export const appendLocationFields = (fd: FormData, coords: GeoPosition) => {
	fd.append('Location.Latitude', String(coords.latitude));
	fd.append('Location.Longitude', String(coords.longitude));
	if (coords.accuracy != null) {
		fd.append('Location.Accuracy', String(coords.accuracy));
	}
};
